import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import useMission from '@/redux/hooks/useMission';
import { getMissionReport, getMissions } from '@/services/mission.service';
import { formatNumber } from '@/utils/formatters';
import { getActiveBadge } from '@/utils/getActiveBadge';
import { Space } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const columns = [
  {
    title: 'No.',
    dataIndex: 'id',
    key: 'id',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Mission name (En)',
    dataIndex: 'name_en',
    key: 'name_en',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Mission name (My)',
    dataIndex: 'name_my',
    key: 'name_my',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Cycle (day)',
    dataIndex: 'cycle',
    key: 'cycle',
    render: (value) => (value ? value : '0'),
  },
  {
    title: 'Number Mission',
    dataIndex: 'number_mission',
    key: 'number_mission',
    render: (value) => (value ? formatNumber(value) : '0'),
  },
  {
    title: 'Heart',
    dataIndex: 'heart',
    key: 'heart',
    render: (value) => (value ? formatNumber(value) : '0'),
  },
  {
    title: 'Point',
    dataIndex: 'point',
    key: 'point',
    render: (value) => (value ? formatNumber(value) : '0'),
  },
  {
    title: 'Key',
    dataIndex: 'total_key',
    key: 'total_key',
    render: (value) => (value ? formatNumber(value) : '0'),
  },
  {
    title: 'Action type',
    dataIndex: 'action',
    key: 'action',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Status',
    dataIndex: 'is_active',
    key: 'is_active',
    render: (isActive) =>
      getActiveBadge(+isActive === 1, {
        activeText: 'Active',
        inactiveText: 'Inactive',
      }),
  },
  {
    title: 'Users Completed',
    dataIndex: 'total_users_completed',
    key: 'total_users_completed',
    render: (value) => (value ? formatNumber(value) : '0'),
  },
  {
    title: 'Users Received Reward',
    dataIndex: 'total_users_received_reward',
    key: 'total_users_received_reward',
    render: (value) => (value ? formatNumber(value) : '0'),
  },
];

const filterOptions = [
  {
    label: 'All',
    value: null,
  },
  {
    label: 'Active',
    value: 1,
  },
  {
    label: 'Inactive',
    value: 2,
  },
];

const START_DATE = dayjs().startOf('week').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function Mission() {
  const navigate = useNavigate();
  // const handleOnCreate = () => {
  //   navigate(PATH.MISSIONS.CREATE);
  // };
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [time, setTime] = useState([START_DATE, END_DATE]);
  const [queryValues, setQueryValues] = useState({
    nameEn: null,
    isActive: null,
    startDate: time[0],
    endDate: time[1],
  });

  const { setMission } = useMission();

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getMissions({
        ...queryValues,
      }),
    ['missions', queryValues]
  );

  const {
    data: reportData,
    isLoading: isReportLoading,
    refetch: refetchReport,
    isRefetching: isReportRefetching,
  } = usePaginatedQuery(
    () =>
      getMissionReport({
        start_date: queryValues.startDate,
        end_date: queryValues.endDate,
      }),
    ['mission-report', queryValues.startDate, queryValues.endDate]
  );

  const handleQuery = () => {
    setQueryValues({
      nameEn: searchValue,
      isActive: selectedFilter ? +selectedFilter : null,
      startDate: time[0],
      endDate: time[1],
    });
  };

  const handleReset = () => {
    setSearchValue(null);
    setSelectedFilter(null);
    setQueryValues({
      nameEn: null,
      isActive: null,
      startDate: START_DATE,
      endDate: END_DATE,
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleOnEdit = (record) => {
    setMission(record);
    navigate(PATH.MISSIONS.UPDATE.replace(':id', record.id));
  };

  const mergedData = useMemo(() => {
    if (!data || !reportData) return data;

    return data.map((mission) => {
      const reportItem = reportData.find((item) => item.mission_id === mission.id);
      return {
        ...mission,
        total_users_completed: reportItem ? reportItem.total_users_completed : 0,
        total_users_received_reward: reportItem ? reportItem.total_users_received_reward : 0,
      };
    });
  }, [data, reportData]);

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Missions" />
      <AppTableQuery
        filterOptions={filterOptions}
        filterPlaceholder="Active status"
        filterLabel="Status"
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        searchValue={searchValue}
        searchPlaceholder="Mission name English"
        setSearchValue={setSearchValue}
        handleQuery={handleQuery}
        handleReset={handleReset}
        time={time}
        setTime={setTime}
        rangePicker
        timeLabel="Period"
        timePlaceholder={['Start date', 'End date']}
        hasDatePicker
        dateLimit={{
          value: 7,
          unit: 'day',
        }}
      />
      <AppTable
        columns={columns}
        dataSource={mergedData || []}
        loading={isLoading || isRefetching}
        hasPagination={false}
        // onCreate={handleOnCreate}
        onRefresh={handleRefresh}
        hasRowAction
        onEditAction={handleOnEdit}
      />
    </Space>
  );
}

export default Mission;
